import { livescoreClient } from '@/api/livescore/client';
import { ApiSearchGet200ResponseDataEntitiesInner } from '@/api/livescore/generated';
import { Input } from '@/components/atoms/Input';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/utils/cn';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

const LivescoreSearch = () => {
  const [isFocus, setIsFocus] = useState(false);
  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value, 500);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ApiSearchGet200ResponseDataEntitiesInner[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const search = async () => {
      if (!debouncedValue) {
        setData([]);
        return;
      }

      setLoading(true);
      const response = await livescoreClient.apiSearchGet({
        q: debouncedValue,
      });

      if (!response.data.success) {
        setError(true);
        return;
      }

      setError(false);

      if (response.data.data.entities) {
        setData(response.data.data.entities);
      }

      setLoading(false);
    };

    search();
  }, [debouncedValue]);

  return (
    <div className="h-10 w-full relative">
      {(isFocus || value !== '') && data.length > 0 ? (
        <div className="fixed z-40 bg-black-transparent w-full h-full top-0 left-0" aria-hidden />
      ) : null}
      <div
        className={cn(
          'absolute top-0 left-0 w-full border rounded-lg transition-colors dark:bg-gray-650 bg-white_theme-50 z-50',
          isFocus || value !== '' ? 'border-primary-600' : 'border-transparent',
        )}
      >
        <Input
          id="livescore-sidebar-search"
          iconLeft={<MagnifyingGlassIcon className="text-gray-400 ml-2" width={16} height={16} />}
          className="!h-10 mt-0 !border-0 text-gray-400 !px-2"
          inputClassName="!border-0"
          placeholder="Szukaj..."
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          value={value}
          onChange={(e) => setValue(e.currentTarget.value)}
        />
        {error ? <p className="text-xs text-gray-400 mt-1">Wystąpił błąd</p> : null}
        <ul className={cn('list-none px-4', isFocus || value !== '' ? 'block' : 'hidden')}>
          {loading ? (
            <>
              {Array.from({ length: 4 }).map((_, index) => (
                <li
                  key={index}
                  className="first:!border-t-0 border-t dark:border-border-400 border-white_theme-100 animate-pulse"
                >
                  <div className="py-2 flex items-center gap-4 w-full">
                    <div className="w-[23px] h-[23px] rounded-full bg-gray-600"></div>
                    <div className="min-h-[40px] flex flex-col justify-center gap-1">
                      <div className="h-3 bg-gray-600 rounded w-16"></div>
                      <div className="h-3 bg-gray-600 rounded w-24"></div>
                    </div>
                  </div>
                </li>
              ))}
            </>
          ) : (
            data.map((item) => (
              <li key={item.id} className="first:!border-t-0 border-t dark:border-border-400 border-white_theme-100">
                <a
                  href={`/livescore/${item.type}/${item.slug ? item.slug : item.id}`}
                  className="py-2 flex items-center gap-4 w-full"
                >
                  <div
                    className={cn(
                      'w-[23px] h-[23px] flex items-center justify-center rounded-full overflow-hidden relative',
                      !item.img_url && 'bg-gray-400',
                      item.type === 'league' && 'bg-gray-50 p-1',
                    )}
                  >
                    <div className="relative w-full h-full">
                      {item.img_url || item.type === 'player' ? (
                        <Image
                          src={item.img_url ?? '/images/player.png'}
                          alt={item.name}
                          layout="fill"
                          objectFit="contain"
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="min-h-[40px] flex flex-col justify-center">
                    {item.gender ? (
                      <span className="text-xxs text-gray-400">{item.gender === 'male' ? 'Mężczyźni' : 'Kobiety'}</span>
                    ) : null}
                    <p className={cn('text-[13px] font-semibold dark:text-white text-black', item.gender && '-mt-1.5')}>
                      {item.name} {item.age_group ? item.age_group : ''}
                    </p>
                  </div>
                </a>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default LivescoreSearch;
